/* HR — Audit Log (R7 fully wired)
 *
 *  Endpoint:
 *    GET /api/audit-logs?entity_type=&entity_id=&action=&by_user_id=&from_at=&to_at=&page=&page_size=
 *
 *  權限：hr / admin only（後端 require_role 限制）
 */

/* action 名稱對應顏色（後端是業務動詞，不是 HTTP method） */
const DESTRUCTIVE_ACTIONS = new Set(['reject', 'cancel', 'delete', 'soft_delete', 'remove']);
const POSITIVE_ACTIONS    = new Set(['approve', 'approve_finalize', 'approve_step', 'pay', 'restore', 'submit']);
const NEUTRAL_ACTIONS     = new Set(['create', 'update', 'patch']);

function actionTone(action) {
  if (DESTRUCTIVE_ACTIONS.has(action)) return HR.down;
  if (POSITIVE_ACTIONS.has(action))    return HR.accent;
  if (NEUTRAL_ACTIONS.has(action))     return '#2C5282';
  return HR.inkSoft;
}

/* Expressive only: same action buckets as actionTone(), rendered as tonal tints (GxT.tint). */
function actionGxTone(action) {
  if (DESTRUCTIVE_ACTIONS.has(action)) return GxCore.tone('down');
  if (POSITIVE_ACTIONS.has(action))    return GxCore.tone('ok');
  if (NEUTRAL_ACTIONS.has(action))     return GxCore.tone('info');
  return GxCore.tone('neutral');
}

function actorColor(name) {
  if (!name) return '#553C7B';
  const palette = ['#D6CEB9', '#C3D6CC', '#E4D5C6', '#D4C8DB', '#DBCEC8', '#C8D0DB'];
  let h = 0;
  for (let i = 0; i < name.length; i++) h = ((h << 5) - h) + name.charCodeAt(i);
  return palette[Math.abs(h) % palette.length];
}

function tryParseJSON(s) {
  if (s == null) return null;
  if (typeof s !== 'string') return s;
  try { return JSON.parse(s); } catch { return { raw: s }; }
}

function AuditRow({ log, expanded, onToggle }) {
  const tone = actionTone(log.action);
  const before = tryParseJSON(log.before_json);
  const after  = tryParseJSON(log.after_json);
  const actor = log.by_username || (log.by_user_id ? `#${log.by_user_id}` : 'system');
  const gTone = actionGxTone(log.action);
  const gc = GxT.color;
  const gxPaneLabel = { fontSize: 12, color: gc.onV, textTransform: 'none', letterSpacing: 0, fontWeight: 700, fontFamily: GxT.font };
  const gxPre = { padding: 12, borderRadius: GxT.radius.input, border: 'none', background: gc.card };

  return (
    <div className="hr-audit-row-wrap" style={{ borderTop: `1px solid ${HR.ruleSoft}` }}>
      <div
        className="hr-audit-row"
        onClick={onToggle}
        style={uiStyle({
          display: 'grid', gridTemplateColumns: '32px 150px 110px 220px 1fr 16px', gap: 12,
          padding: '14px 24px', cursor: 'pointer', alignItems: 'center',
          transition: 'background 120ms',
        }, { padding: '12px 20px', minHeight: 52 })}
        onMouseEnter={(e) => e.currentTarget.style.background = HR.surfaceAlt}
        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
      >
        <div style={uiStyle({
          width: 28, height: 28, borderRadius: '50%',
          background: actorColor(actor), color: HR.ink,
          display: 'grid', placeItems: 'center',
          fontSize: FontSizes.trading_md, fontWeight: 600, fontFamily: HR.serif,
        }, {
          /* system rows get the inverse surface so the white "SYS" label stays legible */
          background: log.by_user_id ? actorColor(actor) : gc.inv, fontSize: 12, fontWeight: 800,
        })}>
          {!log.by_user_id ? (
            <span style={uiStyle({ color: '#fff', fontFamily: HR.mono, fontSize: FontSizes.trading_xxs }, { fontWeight: 700 })}>SYS</span>
          ) : actor.slice(0, 1).toUpperCase()}
        </div>

        <span style={{ fontFamily: HR.mono, fontSize: FontSizes.xxs, color: HR.inkSoft, letterSpacing: uiPick('0.04em', 0) }}>
          {fmtDateTime(log.at)}
        </span>

        <span style={uiStyle({
          display: 'inline-block', padding: '2px 8px', textAlign: 'center',
          background: tone, color: '#fff', borderRadius: 3,
          fontFamily: HR.mono, fontSize: FontSizes.trading_sm, fontWeight: 600, letterSpacing: '0.05em',
        }, {
          padding: '3px 8px', background: gTone.bg, color: gTone.fg, borderRadius: GxT.radius.chipSm,
          fontWeight: 700, letterSpacing: 0, justifySelf: 'start',
        })}>{log.action}</span>

        <span style={{ fontFamily: HR.mono, fontSize: FontSizes.xxs, color: HR.ink }}>
          {log.entity_type}/{log.entity_id}
        </span>

        <div style={{ fontSize: FontSizes.trading_md, color: HR.inkSoft }}>
          <span style={{ color: HR.ink, fontWeight: uiPick(500, 700) }}>{actor}</span>
        </div>

        <Icon name="chevron-down" size={12} stroke={2} className={expanded ? 'rotated' : ''} />
      </div>

      {expanded && (
        <div style={uiStyle({
          padding: '16px 24px 18px 80px', background: HR.surfaceAlt,
          fontFamily: HR.mono, fontSize: FontSizes.trading_xl, lineHeight: 1.6,
        }, { padding: '14px 16px 16px', background: gc.s2, borderRadius: GxT.radius.inner, margin: '0 12px 12px' })}>
          <div style={uiStyle({ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }, { gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 260px), 1fr))', gap: 12 })}>
            <div>
              <div style={uiStyle({
                fontSize: FontSizes.trading_sm, color: HR.inkFaint, textTransform: 'uppercase',
                letterSpacing: '0.1em', marginBottom: 6, fontWeight: 600,
              }, gxPaneLabel)}>Before</div>
              <pre style={uiStyle({
                margin: 0, padding: 10, background: HR.surface, borderRadius: 4,
                border: `1px solid ${HR.rule}`, overflow: 'auto',
                color: before ? HR.down : HR.inkFaint,
              }, gxPre)}>{before ? JSON.stringify(before, null, 2) : '— (新建/系統)'}</pre>
            </div>
            <div>
              <div style={uiStyle({
                fontSize: FontSizes.trading_sm, color: HR.inkFaint, textTransform: 'uppercase',
                letterSpacing: '0.1em', marginBottom: 6, fontWeight: 600,
              }, gxPaneLabel)}>After</div>
              <pre style={uiStyle({
                margin: 0, padding: 10, background: HR.surface, borderRadius: 4,
                border: `1px solid ${HR.rule}`, overflow: 'auto',
                color: after ? HR.accent : HR.inkFaint,
              }, gxPre)}>{after ? JSON.stringify(after, null, 2) : '— (刪除)'}</pre>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function AuditPage() {
  const [filters, setFilters] = React.useState({
    entity_type: '', action: '', by_user_id: '', from_at: '', to_at: '',
  });
  const [page, setPage] = React.useState(1);
  const [total, setTotal] = React.useState(0);
  const [logs, setLogs] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [expandedId, setExpandedId] = React.useState(null);
  const pageSize = 50;

  const fetchLogs = React.useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (filters.entity_type) qs.set('entity_type', filters.entity_type);
      if (filters.action)      qs.set('action', filters.action);
      if (filters.by_user_id)  qs.set('by_user_id', filters.by_user_id);
      if (filters.from_at)     qs.set('from_at', filters.from_at);
      if (filters.to_at)       qs.set('to_at', filters.to_at);
      qs.set('page', String(page));
      qs.set('page_size', String(pageSize));
      const resp = await api('GET', `/audit-logs?${qs.toString()}`);
      setLogs(resp.items || []);
      setTotal(resp.total || 0);
    } catch (e) {
      setLogs([]); setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [filters, page]);

  React.useEffect(() => { fetchLogs(); }, [fetchLogs]);

  /* 7-day stats — load separately (single page, large size). */
  const [stats, setStats] = React.useState({ today: 0, week: 0, destructive: 0, actors: 0 });
  React.useEffect(() => {
    let alive = true;
    (async () => {
      const today = todayStr();
      const weekAgo = nDaysAgoStr(7) + 'T00:00:00Z';
      const resp = await api('GET', `/audit-logs?from_at=${encodeURIComponent(weekAgo)}&page_size=200`).catch(() => ({ items: [] }));
      if (!alive) return;
      const items = resp.items || [];
      const t = items.filter(l => (l.at || '').startsWith(today)).length;
      const w = items.length;
      const d = items.filter(l => DESTRUCTIVE_ACTIONS.has(l.action)).length;
      const actorSet = new Set(items.map(l => l.by_user_id ?? 'system'));
      setStats({ today: t, week: w, destructive: d, actors: actorSet.size });
    })();
    return () => { alive = false; };
  }, []);

  const chips = [];
  if (filters.entity_type) chips.push({ key: 'entity_type', label: '資源類型', value: filters.entity_type });
  if (filters.action)      chips.push({ key: 'action',      label: '動作',     value: filters.action });
  if (filters.by_user_id)  chips.push({ key: 'by_user_id',  label: '操作者 ID', value: filters.by_user_id });
  if (filters.from_at)     chips.push({ key: 'from_at',     label: '起始時間', value: fmtDateTime(filters.from_at) });
  if (filters.to_at)       chips.push({ key: 'to_at',       label: '結束時間', value: fmtDateTime(filters.to_at) });

  /* common entity / action options for quick-add */
  const ENTITY_OPTIONS = [
    'employees', 'departments',
    'leave_request', 'leave_type', 'leave_balance',
    'expense_claim', 'expense_item', 'expense_category',
    'invoice', 'gl_entry', 'payslip', 'payroll_period',
    'punch_record', 'work_schedule', 'attendance_daily',
    'setting', 'role', 'user_role', 'calendar_event',
  ];
  const ACTION_OPTIONS = [
    'create', 'update', 'delete', 'restore',
    'approve', 'approve_step', 'approve_finalize',
    'reject', 'cancel', 'submit', 'pay',
  ];

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <>
      <PageHeader
        eyebrow="系統 · Audit"
        title="審計日誌"
        italic="Audit Log"
        action={
          <Button
            variant="secondary"
            icon="download"
            onClick={() => {
              // Quick CSV export from current page logs
              const header = ['timestamp','actor','action','entity_type','entity_id','description'].join(',');
              const rows = (logs || []).map(l => [
                l.created_at, l.actor_name || l.actor_id, l.action,
                l.entity_type, l.entity_id, (l.description || '').replace(/,/g, '；')
              ].join(','));
              const blob = new Blob([header + '\n' + rows.join('\n')], { type: 'text/csv;charset=utf-8' });
              const a = document.createElement('a');
              a.href = URL.createObjectURL(blob);
              a.download = `audit-log-${todayStr()}.csv`;
              a.click();
            }}
          >匯出 CSV</Button>
        }
      />

      {/* Stats */}
      <div className="rwd-grid-4" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
        <Card eyebrow="Today" title={String(stats.today)} italic="筆操作">
          <div style={{ fontSize: FontSizes.trading_lg, color: HR.inkFaint }}>
            今日截至 {new Date().toLocaleTimeString('zh-TW', { hour: '2-digit', minute: '2-digit' })}
          </div>
        </Card>
        <Card eyebrow="7 Days" title={String(stats.week)} italic="筆操作">
          <div style={{ fontSize: FontSizes.trading_lg, color: HR.inkFaint }}>近 7 日累計（最多顯示 200 筆）</div>
        </Card>
        <Card eyebrow="Actors" title={String(stats.actors)} italic="位操作者">
          <div style={{ fontSize: FontSizes.trading_lg, color: HR.inkFaint }}>含系統自動操作</div>
        </Card>
        <Card eyebrow="Destructive" title={String(stats.destructive)} italic="筆敏感操作">
          <div style={{ fontSize: FontSizes.trading_lg, color: HR.down, fontWeight: uiPick(undefined, 600) }}>包含 reject / delete / cancel</div>
        </Card>
      </div>

      {/* Filter + list */}
      <Card padding="0" eyebrow="Activity" title="操作時間軸" italic={`${total} 筆 · 第 ${page} / ${totalPages} 頁`}>
        <FilterChips
          search={filters.entity_type}
          onSearchChange={(v) => { setFilters(f => ({ ...f, entity_type: v })); setPage(1); }}
          placeholder="搜尋資源類型（e.g. employees, leave_request）…"
          chips={chips}
          onRemove={(k) => { setFilters(f => ({ ...f, [k]: '' })); setPage(1); }}
          addOptions={[
            ...ENTITY_OPTIONS.filter(e => e !== filters.entity_type).map(e => ({ key: `entity_type:${e}`, label: `資源: ${e}` })),
            ...ACTION_OPTIONS.filter(a => a !== filters.action).map(a => ({ key: `action:${a}`, label: `動作: ${a}` })),
          ]}
          onAdd={(k) => {
            const idx = k.indexOf(':');
            const type = k.slice(0, idx);
            const val = k.slice(idx + 1);
            setFilters(f => ({ ...f, [type]: val }));
            setPage(1);
          }}
        />

        {loading ? (
          <SkeletonTable rows={6} />
        ) : logs.length === 0 ? (
          <EmptyState icon="search" title="無符合條件的紀錄" hint={total === 0 ? '系統尚未產生稽核紀錄。' : '請調整篩選條件。'} />
        ) : (
          <>
            <style>{`.rotated { transform: rotate(180deg); transition: transform 200ms; }`}</style>
            {logs.map(log => (
              <AuditRow
                key={log.id}
                log={log}
                expanded={expandedId === log.id}
                onToggle={() => setExpandedId(expandedId === log.id ? null : log.id)}
              />
            ))}
            <Pagination
              page={page}
              pageSize={pageSize}
              total={total}
              onPageChange={setPage}
            />
          </>
        )}
      </Card>
    </>
  );
}

window.AuditPage = AuditPage;
